'use client'

import { useState, useEffect, useRef, useCallback } from 'react'
import { signIn, useSession } from 'next-auth/react'
import { useRouter } from 'next/navigation'
import { Loader2 } from 'lucide-react'
import { validatePassword } from '@/lib/password-policy'
import PasswordChecklist from '@/components/PasswordChecklist'

const TURNSTILE_SITE_KEY = process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY || ''
const TRUSTED_KEY = 'elizium_trusted_device'

const testimonials = [
  { quote: 'Elizium to zdecydowanie coś, czego brakowało na rynku. Prosty, elegancki i skuteczny system do zarządzania salonem.', author: 'Monika K.', role: 'Właścicielka salonu beauty' },
  { quote: 'Od kiedy korzystam z Elizium, moje rezerwacje są w pełni pod kontrolą. Klienci są zadowoleni, ja jestem zadowolona.', author: 'Anna W.', role: 'Kosmetolog' },
  { quote: 'Nie mam recepcji, więc miałam wieczny problem z przypominaniem o wizytach. Elizium rozwiązało to za mnie.', author: 'Katarzyna M.', role: 'Fryzjerka' },
  { quote: 'Przeszliśmy z papierowego kalendarza na Elizium w jeden dzień. Zespół pokochał prostotę tego systemu.', author: 'Paulina S.', role: 'Manager salonu' },
]

const inputStyle: React.CSSProperties = {
  width: '100%', padding: 'clamp(8px, 1vh, 13px) 14px', fontSize: 14, border: '1px solid #d1d5db',
  borderRadius: 10, outline: 'none', color: '#0f172a', background: '#fff', transition: 'border-color 0.15s',
}

function field(id: string, label: string, value: string, onChange: (v: string) => void, placeholder: string, type = 'text', autoComplete = 'off') {
  return (
    <div style={{ display: 'flex', flexDirection: 'column' as const, gap: 10 }}>
      <label htmlFor={id} style={{ fontSize: 'clamp(13px, 1.2vw, 16px)', fontWeight: 500, color: '#374151' }}>{label}</label>
      <input
        id={id} type={type} value={value} onChange={e => onChange(e.target.value)}
        required placeholder={placeholder} autoComplete={autoComplete}
        style={inputStyle}
        onFocus={e => e.target.style.borderColor = '#7B8FA8'}
        onBlur={e => e.target.style.borderColor = '#d1d5db'}
      />
    </div>
  )
}

// 6-digit code input component
function CodeInput({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const refs = useRef<(HTMLInputElement | null)[]>([])
  const digits = value.padEnd(6, '').slice(0, 6).split('')

  const handleChange = (idx: number, val: string) => {
    if (!/^\d?$/.test(val)) return
    const arr = [...digits]
    arr[idx] = val
    onChange(arr.join('').trim())
    if (val && idx < 5) refs.current[idx + 1]?.focus()
  }

  const handleKeyDown = (idx: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !digits[idx] && idx > 0) {
      refs.current[idx - 1]?.focus()
    }
  }

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault()
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6)
    if (pasted) { onChange(pasted); refs.current[Math.min(pasted.length, 5)]?.focus() }
  }

  return (
    <div style={{ display: 'flex', gap: 'clamp(6px, 0.8vw, 10px)', justifyContent: 'center' }}>
      {[0, 1, 2, 3, 4, 5].map(i => (
        <input
          key={i}
          ref={el => { refs.current[i] = el }}
          type="text"
          inputMode="numeric"
          maxLength={1}
          value={digits[i] || ''}
          onChange={e => handleChange(i, e.target.value)}
          onKeyDown={e => handleKeyDown(i, e)}
          onPaste={i === 0 ? handlePaste : undefined}
          style={{
            width: 'clamp(40px, 5vw, 52px)', height: 'clamp(48px, 6vh, 60px)',
            textAlign: 'center', fontSize: 'clamp(20px, 2.5vw, 28px)', fontWeight: 700,
            border: digits[i] ? '2px solid #7B8FA8' : '2px solid #e5e7eb',
            borderRadius: 10, outline: 'none', color: '#0f172a', background: '#f9fafb',
            transition: 'border-color 0.15s',
          }}
          onFocus={e => e.target.style.borderColor = '#7B8FA8'}
          onBlur={e => { if (!digits[i]) e.target.style.borderColor = '#e5e7eb' }}
        />
      ))}
    </div>
  )
}

type Mode = 'login' | 'register' | 'verify-login' | 'verify-register' | 'forgot-password'

export default function AuthPage() {
  const [mode, setMode] = useState<Mode>('login')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [password2, setPassword2] = useState('')
  const [salonName, setSalonName] = useState('')
  const [nip, setNip] = useState('')
  // Podgląd z GUS REGON po wpisaniu NIP — użytkownik od razu widzi, czy numer
  // wskazuje na jego firmę (autofill właściwych danych robi onboarding).
  const [gusHint, setGusHint] = useState<{ name: string; city: string; closed: boolean } | null>(null)
  const [gusMiss, setGusMiss] = useState(false)
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  // Kod promocyjny — opcjonalne pole rejestracji. Przycisk "Zastosuj" waliduje
  // kod od ręki (UX — pewność że działa); rejestracja i tak waliduje ponownie.
  const [promoCode, setPromoCode] = useState('')
  // null = nie sprawdzano, 'checking' | 'valid' | 'invalid'
  const [promoStatus, setPromoStatus] = useState<null | 'checking' | 'valid' | 'invalid'>(null)
  const [promoGrant, setPromoGrant] = useState<number | null>(null)

  const handleCheckPromo = async () => {
    if (!promoCode.trim()) return
    setPromoStatus('checking')
    try {
      const res = await fetch('/api/auth/check-promo-code', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: promoCode }),
      })
      const d = await res.json()
      if (d.valid) { setPromoStatus('valid'); setPromoGrant(d.smsGrant ?? null) }
      else { setPromoStatus('invalid'); setPromoGrant(null) }
    } catch { setPromoStatus('invalid'); setPromoGrant(null) }
  }
  const [code, setCode] = useState('')
  const [trustDevice, setTrustDevice] = useState(true)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [loading, setLoading] = useState(false)
  const [activeQuote, setActiveQuote] = useState(0)
  const [captchaToken, setCaptchaToken] = useState('')
  const [countdown, setCountdown] = useState(0)
  // Dokumenty prawne — aktywne wersje z API; 3 osobne checkboxy, backend egzekwuje
  const [legalDocs, setLegalDocs] = useState<Array<{ type: string; label: string; url: string; version: string }>>([])
  const [legalAccepted, setLegalAccepted] = useState<Record<string, boolean>>({})

  useEffect(() => {
    fetch('/api/public/legal')
      .then(r => r.json())
      .then(d => setLegalDocs(Array.isArray(d?.documents) ? d.documents : []))
      .catch(() => setLegalDocs([]))
  }, [])
  const allLegalAccepted = legalDocs.every(d => legalAccepted[d.type])
  const captchaRef = useRef<HTMLDivElement>(null)
  const widgetIdRef = useRef<string | null>(null)
  const { update: updateSession } = useSession()
  const router = useRouter()

  // Load Turnstile script
  useEffect(() => {
    if (!TURNSTILE_SITE_KEY) return
    if (document.getElementById('cf-turnstile-script')) return
    const s = document.createElement('script')
    s.id = 'cf-turnstile-script'
    s.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'
    s.async = true
    document.head.appendChild(s)
  }, [])

  // Render/reset Turnstile widget
  const renderCaptcha = useCallback(() => {
    if (!TURNSTILE_SITE_KEY || !captchaRef.current) return
    const w = (window as any).turnstile
    if (!w) { setTimeout(renderCaptcha, 200); return }
    if (widgetIdRef.current !== null) { w.remove(widgetIdRef.current) }
    setCaptchaToken('')
    widgetIdRef.current = w.render(captchaRef.current, {
      sitekey: TURNSTILE_SITE_KEY,
      callback: (token: string) => setCaptchaToken(token),
      'expired-callback': () => setCaptchaToken(''),
      theme: 'light',
    })
  }, [])

  useEffect(() => {
    if (mode === 'login' || mode === 'register') renderCaptcha()
  }, [mode, renderCaptcha])

  // Podgląd firmy z GUS po wpisaniu pełnego NIP (debounce 600 ms).
  useEffect(() => {
    setGusHint(null)
    setGusMiss(false)
    const digits = nip.replace(/^\s*PL/i, '').replace(/[^0-9]/g, '')
    if (digits.length !== 10) return
    const t = setTimeout(() => {
      fetch(`/api/gus/lookup?nip=${digits}`)
        .then(r => r.ok ? r.json() : r.status === 404 ? { found: false } : null)
        .then(d => {
          if (!d) return
          if (d.found) setGusHint({ name: d.name, city: d.city, closed: !!d.closed })
          else setGusMiss(true)
        })
        .catch(() => {})
    }, 600)
    return () => clearTimeout(t)
  }, [nip])

  useEffect(() => {
    const interval = setInterval(() => setActiveQuote(q => (q + 1) % testimonials.length), 5000)
    return () => clearInterval(interval)
  }, [])

  // Resend countdown
  useEffect(() => {
    if (countdown <= 0) return
    const t = setTimeout(() => setCountdown(c => c - 1), 1000)
    return () => clearTimeout(t)
  }, [countdown])

  const switchMode = () => {
    setMode(m => m === 'login' || m === 'verify-login' ? 'register' : 'login')
    setError(''); setSuccess(''); setCode('')
  }

  const verifyCaptcha = async (): Promise<boolean> => {
    if (!TURNSTILE_SITE_KEY || !captchaToken) return !TURNSTILE_SITE_KEY
    const res = await fetch('/api/auth/verify-captcha', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token: captchaToken }),
    })
    return res.ok
  }

  const resetCaptcha = () => {
    setCaptchaToken('')
    const w = (window as any).turnstile
    if (w && widgetIdRef.current !== null) w.reset(widgetIdRef.current)
  }

  const sendCode = async (targetEmail: string, type: 'login' | 'register') => {
    const res = await fetch('/api/auth/send-code', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: targetEmail, type }),
    })
    if (!res.ok) {
      const data = await res.json()
      throw new Error(data.error || 'Błąd wysyłki kodu')
    }
    setCountdown(30)
  }

  // ---- LOGIN FLOW ----
  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true); setError('')
    try {
      if (!await verifyCaptcha()) { setError('Weryfikacja captcha nie powiodła się'); resetCaptcha(); setLoading(false); return }
      
      // First verify credentials
      const res = await signIn('credentials', { email, password, redirect: false })
      if (res?.error) { setError('Nieprawidłowy login lub hasło'); resetCaptcha(); setLoading(false); return }

      // Check if device is trusted
      const trustedToken = localStorage.getItem(TRUSTED_KEY)
      if (trustedToken) {
        const check = await fetch('/api/auth/check-trusted', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email, trustedToken }),
        })
        const checkData = await check.json()
        if (checkData.trusted) { await updateSession({ pending2FA: false }); router.push('/dashboard'); return }
      }

      // Send verification code
      await sendCode(email, 'login')
      setMode('verify-login')
      setCode('')
      setLoading(false)
    } catch (err: any) { setError(err.message || 'Błąd połączenia z serwerem'); resetCaptcha(); setLoading(false) }
  }

  // ---- VERIFY LOGIN CODE ----
  const handleVerifyLogin = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true); setError('')
    try {
      const res = await fetch('/api/auth/verify-code', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, code, type: 'login', trustDevice }),
      })
      const data = await res.json()
      if (!res.ok) { setError(data.error || 'Nieprawidłowy kod'); setLoading(false); return }
      
      // Save trusted device token
      if (data.trustedToken) {
        localStorage.setItem(TRUSTED_KEY, data.trustedToken)
      }
      
      // Unlock session — clear pending2FA flag
      await updateSession({ pending2FA: false })
      
      router.push('/dashboard')
    } catch { setError('Błąd połączenia z serwerem'); setLoading(false) }
  }

  // ---- REGISTER FLOW ----
  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true); setError(''); setSuccess('')
    {
      // NIP: suma kontrolna (wagi 6,5,7,2,3,4,5,6,7 mod 11) — walidacja przed wysyłką kodu
      const n = nip.replace(/^\s*PL/i, '').replace(/[^0-9]/g, '')
      if (!/^\d{10}$/.test(n)) { setError('Podaj poprawny NIP (10 cyfr)'); setLoading(false); return }
      const w = [6, 5, 7, 2, 3, 4, 5, 6, 7]
      const sum = w.reduce((a, x, i) => a + x * parseInt(n[i], 10), 0)
      if (sum % 11 === 10 || sum % 11 !== parseInt(n[9], 10)) { setError('Nieprawidłowy NIP — sprawdź, czy nie ma literówki'); setLoading(false); return }
    }
    if (legalDocs.length > 0 && !allLegalAccepted) { setError('Zaakceptuj wszystkie wymagane dokumenty'); setLoading(false); return }
    if (password !== password2) { setError('Hasła nie są identyczne'); setLoading(false); return }
    { const er = validatePassword(password); if (er) { setError(er); setLoading(false); return } }
    try {
      // Send verification code first (before creating account)
      await sendCode(email, 'register')
      setMode('verify-register')
      setCode('')
      setLoading(false)
    } catch (err: any) { setError(err.message || 'Błąd wysyłki kodu'); setLoading(false) }
  }

  // ---- VERIFY REGISTER CODE ----
  const handleVerifyRegister = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true); setError('')
    try {
      // Verify code
      const verifyRes = await fetch('/api/auth/verify-code', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, code, type: 'register' }),
      })
      if (!verifyRes.ok) { const d = await verifyRes.json(); setError(d.error || 'Nieprawidłowy kod'); setLoading(false); return }

      // Create account
      const res = await fetch('/api/auth/register', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          salonName, nip, firstName, lastName, email, password, captchaToken: 'verified',
          promoCode: promoCode.trim() || undefined,
          acceptedDocuments: legalDocs.filter(d => legalAccepted[d.type]).map(d => ({ type: d.type, version: d.version })),
        }),
      })
      const data = await res.json()
      if (!res.ok) { setError(data.error || 'Błąd rejestracji'); setLoading(false); return }

      // Auto-login. Pytanie o liczbę lokalizacji zadaje onboarding (krok 2).
      const loginRes = await signIn('credentials', { email, password, redirect: false })
      if (loginRes?.ok) { router.push('/dashboard') }
      else { setMode('login'); setLoading(false) }
    } catch { setError('Błąd połączenia z serwerem'); setLoading(false) }
  }

  const handleResend = async () => {
    if (countdown > 0) return
    setError('')
    try {
      const type = mode === 'verify-login' ? 'login' : 'register'
      await sendCode(email, type as 'login' | 'register')
      setSuccess('Kod wysłany ponownie')
      setTimeout(() => setSuccess(''), 3000)
    } catch (err: any) { setError(err.message) }
  }

  // Determine right side CTA text
  const isVerify = mode === 'verify-login' || mode === 'verify-register'
  const isLoginSide = mode === 'login' || mode === 'verify-login' || mode === 'forgot-password'

  return (
    <div className="login-wrapper" style={{ display: 'flex', minHeight: '100vh', flexDirection: 'column' }}>
      <div className="login-content" style={{ display: 'flex', flex: 1 }}>

        {/* LEFT SIDE */}
        <div className="login-left" style={{ width: '40%', display: 'flex', flexDirection: 'column', alignItems: 'center', padding: 'clamp(16px, 3vh, 48px) clamp(20px, 3vw, 64px)', background: '#ffffff', position: 'relative' }}>
          {/* margin: 'auto' zamiast justifyContent: 'center' — centruje gdy jest miejsce,
              a przy przepełnieniu (mały ekran, rejestracja) nie ucina góry formularza
              i pozwala przewijać całą stronę. */}
          <div style={{ maxWidth: 380, width: '100%', margin: 'auto' }}>
            {/* Logo */}
            <div style={{ marginBottom: 'clamp(16px, 2vh, 40px)' }}>
              <span style={{ fontFamily: 'Urbanist, sans-serif', fontSize: 'clamp(28px, 3.5vw, 58px)', fontWeight: 700, letterSpacing: 'clamp(2px, 0.3vw, 6px)', color: '#0f172a', textTransform: 'uppercase' as const }}>ELIZIUM</span>
            </div>

            {/* ====== LOGIN FORM ====== */}
            {mode === 'login' && (
              <>
                <h1 style={{ fontSize: 'clamp(18px, 2vw, 26px)', fontWeight: 700, color: '#0f172a', marginBottom: 'clamp(16px, 2vh, 32px)' }}>Zaloguj się do systemu</h1>
                <form onSubmit={handleLogin} style={{ display: 'flex', flexDirection: 'column', gap: 'clamp(14px, 1.8vh, 22px)' }}>
                  {error && <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '10px 16px', color: '#dc2626', fontSize: 14 }}>{error}</div>}
                  {field('email', 'Adres e-mail', email, setEmail, 'jan@salon.pl', 'email', 'username')}
                  {field('password', 'Hasło', password, setPassword, 'Wprowadź hasło', 'password', 'current-password')}
                  {TURNSTILE_SITE_KEY && <div ref={captchaRef} />}
                  <button type="submit" disabled={loading || (!!TURNSTILE_SITE_KEY && !captchaToken)} style={{ width: '100%', padding: 'clamp(10px, 1.2vh, 13px) 0', fontSize: 15, fontWeight: 600, color: '#fff', background: '#7B8FA8', border: 'none', borderRadius: 10, cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.7 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                    {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                    {loading ? 'Logowanie...' : 'Zaloguj się'}
                  </button>
                  <button type="button" onClick={() => { setMode('forgot-password' as Mode); setError(''); setSuccess('') }} style={{ background: 'none', border: 'none', fontSize: 15, color: '#7B8FA8', textDecoration: 'underline', textUnderlineOffset: 3, cursor: 'pointer', padding: 0 }}>Nie pamiętam hasła</button>
                </form>
              </>
            )}

            {/* ====== FORGOT PASSWORD ====== */}
            {mode === 'forgot-password' && (
              <>
                <h1 style={{ fontSize: 'clamp(18px, 2vw, 26px)', fontWeight: 700, color: '#0f172a', marginBottom: 8 }}>Nie pamiętasz hasła?</h1>
                <p style={{ fontSize: 14, color: '#6b7280', marginBottom: 'clamp(20px, 2.5vh, 32px)', lineHeight: 1.5 }}>
                  Podaj adres e-mail powiązany z Twoim kontem. Wyślemy Ci link do zresetowania hasła.
                </p>
                <form onSubmit={async (e) => {
                  e.preventDefault()
                  setLoading(true); setError(''); setSuccess('')
                  try {
                    const res = await fetch('/api/auth/forgot-password', {
                      method: 'POST', headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({ email }),
                    })
                    if (!res.ok) {
                      const data = await res.json()
                      setError(data.error || 'Błąd wysyłki')
                    } else {
                      setSuccess('Jeśli konto z tym adresem istnieje, wysłaliśmy link do resetowania hasła. Sprawdź swoją skrzynkę.')
                    }
                  } catch { setError('Błąd połączenia z serwerem') }
                  setLoading(false)
                }} style={{ display: 'flex', flexDirection: 'column', gap: 'clamp(14px, 1.8vh, 22px)' }}>
                  {error && <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '10px 16px', color: '#dc2626', fontSize: 14 }}>{error}</div>}
                  {success && <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: '10px 16px', color: '#16a34a', fontSize: 14 }}>{success}</div>}
                  {!success && (
                    <>
                      {field('resetEmail', 'Adres e-mail', email, setEmail, 'jan@salon.pl', 'email', 'email')}
                      <button type="submit" disabled={loading} style={{ width: '100%', padding: 'clamp(10px, 1.2vh, 13px) 0', fontSize: 15, fontWeight: 600, color: '#fff', background: '#7B8FA8', border: 'none', borderRadius: 10, cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.7 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                        {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                        {loading ? 'Wysyłanie...' : 'Wyślij link resetujący'}
                      </button>
                    </>
                  )}
                  <button type="button" onClick={() => { setMode('login'); setError(''); setSuccess('') }} style={{ background: 'none', border: 'none', fontSize: 14, color: '#7B8FA8', cursor: 'pointer', textDecoration: 'underline', textUnderlineOffset: 3, padding: 0 }}>
                    Wróć do logowania
                  </button>
                </form>
              </>
            )}

            {/* ====== VERIFY LOGIN ====== */}
            {mode === 'verify-login' && (
              <>
                <h1 style={{ fontSize: 'clamp(18px, 2vw, 26px)', fontWeight: 700, color: '#0f172a', marginBottom: 8 }}>Musimy wiedzieć,<br/>że to na pewno Ty!</h1>
                <p style={{ fontSize: 14, color: '#6b7280', marginBottom: 'clamp(20px, 2.5vh, 32px)', lineHeight: 1.5 }}>
                  Wysłaliśmy na Twój adres e-mail 6 cyfrowy kod, który jest ważny 15 minut.
                </p>
                <form onSubmit={handleVerifyLogin} style={{ display: 'flex', flexDirection: 'column', gap: 'clamp(16px, 2vh, 24px)' }}>
                  {error && <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '10px 16px', color: '#dc2626', fontSize: 14 }}>{error}</div>}
                  {success && <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: '10px 16px', color: '#16a34a', fontSize: 14 }}>{success}</div>}
                  <CodeInput value={code} onChange={setCode} />
                  <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', fontSize: 14, color: '#374151' }}>
                    <input type="checkbox" checked={trustDevice} onChange={e => setTrustDevice(e.target.checked)} style={{ width: 18, height: 18, accentColor: '#7B8FA8', cursor: 'pointer' }} />
                    Jestem na zaufanym komputerze
                  </label>
                  <button type="submit" disabled={loading || code.length < 6} style={{ width: '100%', padding: 'clamp(10px, 1.2vh, 13px) 0', fontSize: 15, fontWeight: 600, color: '#fff', background: '#7B8FA8', border: 'none', borderRadius: 10, cursor: loading || code.length < 6 ? 'not-allowed' : 'pointer', opacity: loading || code.length < 6 ? 0.7 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                    {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                    {loading ? 'Weryfikacja...' : 'Zaloguj'}
                  </button>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <button type="button" onClick={handleResend} disabled={countdown > 0} style={{ background: 'none', border: 'none', fontSize: 14, color: countdown > 0 ? '#9ca3af' : '#7B8FA8', cursor: countdown > 0 ? 'default' : 'pointer', textDecoration: countdown > 0 ? 'none' : 'underline', textUnderlineOffset: 3, padding: 0 }}>
                      {countdown > 0 ? `Wyślij ponownie (${countdown}s)` : 'Wyślij ponownie'}
                    </button>
                    <button type="button" onClick={() => { setMode('login'); setError(''); setCode('') }} style={{ background: 'none', border: 'none', fontSize: 14, color: '#7B8FA8', cursor: 'pointer', textDecoration: 'underline', textUnderlineOffset: 3, padding: 0 }}>
                      Anuluj
                    </button>
                  </div>
                </form>
              </>
            )}

            {/* ====== REGISTER FORM ====== */}
            {mode === 'register' && (
              <>
                <h1 style={{ fontSize: 'clamp(18px, 2vw, 26px)', fontWeight: 700, color: '#0f172a', marginBottom: 6 }}>Załóż konto</h1>
                <p style={{ fontSize: 'clamp(13px, 1.2vw, 15px)', color: '#6b7280', marginBottom: 'clamp(16px, 2vh, 32px)' }}>30 dni za darmo. Bez karty kredytowej.</p>
                <form onSubmit={handleRegister} style={{ display: 'flex', flexDirection: 'column', gap: 'clamp(12px, 1.5vh, 18px)' }}>
                  {error && <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '10px 16px', color: '#dc2626', fontSize: 14 }}>{error}</div>}
                  {field('salonName', 'Nazwa salonu', salonName, setSalonName, 'np. Studio Urody Anna')}
                  {field('nip', 'NIP firmy', nip, setNip, '1234567890')}
                  {gusHint && (
                    <p style={{ fontSize: 13, color: gusHint.closed ? '#d97706' : '#16a34a', margin: '-6px 0 0', lineHeight: 1.4 }}>
                      {gusHint.closed
                        ? `⚠️ W rejestrze REGON: ${gusHint.name} (${gusHint.city}) — działalność oznaczona jako zakończona`
                        : `✓ W rejestrze REGON: ${gusHint.name} (${gusHint.city})`}
                    </p>
                  )}
                  {gusMiss && (
                    <p style={{ fontSize: 13, color: '#d97706', margin: '-6px 0 0', lineHeight: 1.4 }}>
                      Nie znaleźliśmy tego NIP-u w rejestrze REGON — sprawdź, czy nie ma literówki.
                    </p>
                  )}
                  <div style={{ display: 'flex', gap: 16 }}>
                    <div style={{ flex: 1 }}>{field('firstName', 'Imię', firstName, setFirstName, 'Jan')}</div>
                    <div style={{ flex: 1 }}>{field('lastName', 'Nazwisko', lastName, setLastName, 'Kowalski')}</div>
                  </div>
                  {field('regEmail', 'Adres e-mail', email, setEmail, 'jan@salon.pl', 'email', 'email')}
                  {field('regPassword', 'Hasło', password, setPassword, 'Min. 8 znaków', 'password', 'new-password')}
                  <PasswordChecklist password={password} />
                  {field('regPassword2', 'Powtórz hasło', password2, setPassword2, 'Powtórz hasło', 'password', 'new-password')}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    <label htmlFor="promoCode" style={{ fontSize: 'clamp(13px, 1.2vw, 16px)', fontWeight: 500, color: '#374151' }}>
                      Kod promocyjny <span style={{ fontWeight: 400, color: '#9ca3af' }}>(opcjonalnie)</span>
                    </label>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <input
                        id="promoCode" type="text" value={promoCode}
                        onChange={e => { setPromoCode(e.target.value.toUpperCase()); setPromoStatus(null); setPromoGrant(null) }}
                        onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleCheckPromo() } }}
                        placeholder="np. PROMO2026" autoComplete="off" maxLength={64}
                        style={{
                          ...inputStyle, flex: 1,
                          ...(promoStatus === 'valid' ? { borderColor: '#16a34a' } : {}),
                          ...(promoStatus === 'invalid' ? { borderColor: '#dc2626' } : {}),
                        }}
                        onFocus={e => e.target.style.borderColor = '#7B8FA8'}
                        onBlur={e => e.target.style.borderColor = promoStatus === 'valid' ? '#16a34a' : promoStatus === 'invalid' ? '#dc2626' : '#d1d5db'}
                      />
                      <button
                        type="button" onClick={handleCheckPromo}
                        disabled={!promoCode.trim() || promoStatus === 'checking'}
                        style={{
                          padding: '0 18px', borderRadius: 10, border: '1.5px solid #7B8FA8',
                          background: 'transparent', color: '#7B8FA8', fontWeight: 600,
                          fontSize: 'clamp(13px, 1.1vw, 15px)', whiteSpace: 'nowrap',
                          cursor: !promoCode.trim() || promoStatus === 'checking' ? 'default' : 'pointer',
                          opacity: !promoCode.trim() ? 0.5 : 1, flexShrink: 0,
                        }}
                      >
                        {promoStatus === 'checking' ? 'Sprawdzam…' : 'Zastosuj'}
                      </button>
                    </div>
                    {promoStatus === 'valid' && (
                      <div style={{ fontSize: 13, color: '#16a34a', fontWeight: 500 }}>
                        ✓ Kod działa{promoGrant ? ` — otrzymasz ${promoGrant} darmowych SMS` : ''}
                      </div>
                    )}
                    {promoStatus === 'invalid' && (
                      <div style={{ fontSize: 13, color: '#dc2626', fontWeight: 500 }}>
                        Nieprawidłowy kod promocyjny
                      </div>
                    )}
                  </div>
                  {legalDocs.length > 0 && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {legalDocs.map(d => (
                        <label key={d.type} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, fontSize: 13, color: '#374151', cursor: 'pointer', lineHeight: 1.5 }}>
                          <input
                            type="checkbox"
                            checked={!!legalAccepted[d.type]}
                            onChange={e => setLegalAccepted(prev => ({ ...prev, [d.type]: e.target.checked }))}
                            style={{ width: 17, height: 17, accentColor: '#7B8FA8', cursor: 'pointer', marginTop: 2, flexShrink: 0 }}
                          />
                          <span>
                            Akceptuję{' '}
                            <a href={d.url} target="_blank" rel="noopener noreferrer" style={{ color: '#7B8FA8', textDecoration: 'underline' }}>
                              {d.label.toLowerCase()}
                            </a>{' '}
                            (wersja {d.version})
                          </span>
                        </label>
                      ))}
                    </div>
                  )}
                  {TURNSTILE_SITE_KEY && <div ref={captchaRef} />}
                  <button type="submit" disabled={loading || (!!TURNSTILE_SITE_KEY && !captchaToken) || (legalDocs.length > 0 && !allLegalAccepted)} style={{ width: '100%', padding: 'clamp(10px, 1.2vh, 13px) 0', fontSize: 15, fontWeight: 600, color: '#fff', background: '#7B8FA8', border: 'none', borderRadius: 10, cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.7 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginTop: 4 }}>
                    {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                    {loading ? 'Wysyłanie kodu...' : 'Załóż konto — 30 dni za darmo'}
                  </button>

                </form>
              </>
            )}

            {/* ====== VERIFY REGISTER ====== */}
            {mode === 'verify-register' && (
              <>
                <h1 style={{ fontSize: 'clamp(18px, 2vw, 26px)', fontWeight: 700, color: '#0f172a', marginBottom: 8 }}>Potwierdź swój adres email</h1>
                <p style={{ fontSize: 14, color: '#6b7280', marginBottom: 'clamp(20px, 2.5vh, 32px)', lineHeight: 1.5 }}>
                  Wysłaliśmy na <strong>{email}</strong> 6 cyfrowy kod, który jest ważny 15 minut.
                </p>
                <form onSubmit={handleVerifyRegister} style={{ display: 'flex', flexDirection: 'column', gap: 'clamp(16px, 2vh, 24px)' }}>
                  {error && <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '10px 16px', color: '#dc2626', fontSize: 14 }}>{error}</div>}
                  {success && <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: '10px 16px', color: '#16a34a', fontSize: 14 }}>{success}</div>}
                  <CodeInput value={code} onChange={setCode} />
                  <button type="submit" disabled={loading || code.length < 6} style={{ width: '100%', padding: 'clamp(10px, 1.2vh, 13px) 0', fontSize: 15, fontWeight: 600, color: '#fff', background: '#7B8FA8', border: 'none', borderRadius: 10, cursor: loading || code.length < 6 ? 'not-allowed' : 'pointer', opacity: loading || code.length < 6 ? 0.7 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                    {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                    {loading ? 'Tworzenie konta...' : 'Potwierdź i załóż konto'}
                  </button>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <button type="button" onClick={handleResend} disabled={countdown > 0} style={{ background: 'none', border: 'none', fontSize: 14, color: countdown > 0 ? '#9ca3af' : '#7B8FA8', cursor: countdown > 0 ? 'default' : 'pointer', textDecoration: countdown > 0 ? 'none' : 'underline', textUnderlineOffset: 3, padding: 0 }}>
                      {countdown > 0 ? `Wyślij ponownie (${countdown}s)` : 'Wyślij ponownie'}
                    </button>
                    <button type="button" onClick={() => { setMode('register'); setError(''); setCode('') }} style={{ background: 'none', border: 'none', fontSize: 14, color: '#7B8FA8', cursor: 'pointer', textDecoration: 'underline', textUnderlineOffset: 3, padding: 0 }}>
                      Anuluj
                    </button>
                  </div>
                </form>
              </>
            )}

          </div>

          {/* Copyright — w przepływie strony (nie absolute), więc nie nachodzi na treść */}
          <div className="login-footer" style={{ width: '100%', maxWidth: 380, paddingTop: 24, fontSize: 12, color: '#9ca3af' }}>
            Copyright {new Date().getFullYear()}. Elizium. Wszystkie prawa zastrzeżone
          </div>
        </div>

        {/* RIGHT SIDE */}
        <div className="login-right" style={{ flex: 1, background: 'linear-gradient(135deg, #DBEDF7 0%, #C4DBEF 40%, #B2CAEE 100%)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '48px 64px', position: 'relative', overflow: 'hidden' }}>
          <div style={{ position: 'absolute', top: -100, right: -100, width: 400, height: 400, borderRadius: '50%', background: 'rgba(255,255,255,0.35)' }} />
          <div style={{ position: 'absolute', bottom: -150, left: -100, width: 500, height: 500, borderRadius: '50%', background: 'rgba(255,255,255,0.2)' }} />
          <div style={{ position: 'absolute', top: '40%', left: '60%', width: 300, height: 300, borderRadius: '50%', background: 'rgba(178,202,238,0.3)' }} />

          {/* CTA */}
          <div style={{ textAlign: 'center', marginBottom: 48, zIndex: 1 }}>
            <h2 style={{ fontSize: 28, fontWeight: 700, color: '#3d3229', marginBottom: 16 }}>
              {isLoginSide ? 'Nie masz jeszcze konta?' : 'Masz już konto?'}
            </h2>
            <button onClick={switchMode} style={{ display: 'inline-block', padding: '12px 32px', fontSize: 15, fontWeight: 600, color: '#fff', background: '#7B8FA8', border: '2px solid #6B7F98', borderRadius: 8, cursor: 'pointer', transition: 'background 0.15s' }}>
              {isLoginSide ? 'Załóż konto' : 'Zaloguj się'}
            </button>
          </div>

          {/* App mockup */}
          <div className="app-mockup" style={{ width: '100%', maxWidth: 520, background: 'rgba(255,255,255,0.6)', borderRadius: 16, padding: 20, backdropFilter: 'blur(20px)', border: '1px solid rgba(255,255,255,0.4)', marginBottom: 48, zIndex: 1 }}>
            <div style={{ background: '#ffffff', borderRadius: 10, padding: 24, minHeight: 200, display: 'flex', flexDirection: 'column', gap: 16, boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
              <div style={{ display: 'flex', gap: 8 }}>
                <div style={{ width: 12, height: 12, borderRadius: '50%', background: '#ef4444' }} />
                <div style={{ width: 12, height: 12, borderRadius: '50%', background: '#eab308' }} />
                <div style={{ width: 12, height: 12, borderRadius: '50%', background: '#22c55e' }} />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
                {['Wizyty dziś', 'Klienci', 'Przychód'].map((label, i) => (
                  <div key={label} style={{ background: '#f8f5f2', borderRadius: 8, padding: '16px 14px', border: '1px solid #ebe5df' }}>
                    <p style={{ fontSize: 11, color: '#9a8b7d', marginBottom: 6 }}>{label}</p>
                    <p style={{ fontSize: 22, fontWeight: 700, color: '#3d3229' }}>{['12', '847', '4,2k'][i]}</p>
                  </div>
                ))}
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                {[...Array(5)].map((_, i) => (
                  <div key={i} style={{ flex: 1, background: 'rgba(123,143,168,0.3)', borderRadius: 4, height: [48, 72, 56, 88, 64][i], alignSelf: 'flex-end' }} />
                ))}
              </div>
            </div>
          </div>

          {/* Testimonial */}
          <div style={{ width: '100%', maxWidth: 520, background: 'rgba(255,255,255,0.6)', borderRadius: 12, padding: '28px 32px', backdropFilter: 'blur(20px)', border: '1px solid rgba(255,255,255,0.4)', zIndex: 1 }}>
            <div style={{ fontSize: 28, color: '#7B8FA8', marginBottom: 12, lineHeight: 1 }}>&ldquo;</div>
            <p style={{ fontSize: 15, color: '#4a3f35', lineHeight: 1.6, marginBottom: 16, minHeight: 48 }}>
              {testimonials[activeQuote].quote}
            </p>
            <p style={{ fontSize: 14, fontWeight: 600, color: '#3d3229' }}>{testimonials[activeQuote].author}</p>
            <p style={{ fontSize: 13, color: '#9a8b7d' }}>{testimonials[activeQuote].role}</p>
            <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
              {testimonials.map((_, i) => (
                <button key={i} onClick={() => setActiveQuote(i)} style={{ width: 10, height: 10, borderRadius: '50%', border: 'none', background: i === activeQuote ? '#3d3229' : 'rgba(61,50,41,0.25)', cursor: 'pointer', transition: 'background 0.2s' }} />
              ))}
            </div>
          </div>
        </div>

      </div>

      <style>{`
        @media (max-width: 1100px) {
          .login-left { width: 50% !important; min-width: unset !important; }
          .login-right .app-mockup { display: none !important; }
        }
        @media (max-width: 900px) {
          .login-wrapper { flex-direction: column !important; }
          .login-content { flex-direction: column !important; }
          .login-left { width: 100% !important; min-width: unset !important; padding: 40px 24px 32px !important; min-height: auto !important; }
          .login-right { padding: 32px 24px !important; }
          .login-footer { text-align: center !important; }
        }
      `}</style>
    </div>
  )
}
